import { useCallback, useRef, useState } from "react";
import { DebatePrep, requestDebatePrep } from "../firebase/prepare";

export type DebatePrepStatus = "idle" | "loading" | "ready" | "error";

export interface DebatePrepState {
  /** The finished prep for this question, if there is one. */
  result: DebatePrep | null;
  status: DebatePrepStatus;
  /** Start preparing in the background; does nothing if it's done or under way. */
  prepare: (question: string) => void;
  /** Wait for the prep (starting it if needed); null if it failed. */
  ensure: (question: string) => Promise<DebatePrep | null>;
  /** Try again after a failure. */
  retry: (question: string) => void;
}

const MAX_CACHED = 12;

/**
 * Background panel picks and question classification, cached per question so
 * moving back and forth through setup never repeats a call.
 */
export function useDebatePrep(question: string): DebatePrepState {
  const cache = useRef(new Map<string, DebatePrep>());
  const inflight = useRef(new Map<string, Promise<DebatePrep | null>>());
  const failed = useRef(new Set<string>());
  const [, setVersion] = useState(0);
  const bump = () => setVersion((v) => v + 1);

  const ensure = useCallback((raw: string): Promise<DebatePrep | null> => {
    const key = raw.trim();
    if (!key) return Promise.resolve(null);
    const cached = cache.current.get(key);
    if (cached) return Promise.resolve(cached);
    const running = inflight.current.get(key);
    if (running) return running;
    failed.current.delete(key);
    const promise = requestDebatePrep(key)
      .then((prep) => {
        cache.current.set(key, prep);
        if (cache.current.size > MAX_CACHED) cache.current.delete(cache.current.keys().next().value!);
        return prep;
      })
      .catch((err) => {
        console.error("prepareDebate failed:", err);
        failed.current.add(key);
        return null;
      })
      .finally(() => {
        inflight.current.delete(key);
        bump();
      });
    inflight.current.set(key, promise);
    bump();
    return promise;
  }, []);

  const prepare = useCallback((raw: string) => {
    const key = raw.trim();
    if (key && !failed.current.has(key)) void ensure(key);
  }, [ensure]);

  const retry = useCallback((raw: string) => {
    failed.current.delete(raw.trim());
    void ensure(raw);
  }, [ensure]);

  const key = question.trim();
  const result = cache.current.get(key) ?? null;
  const status: DebatePrepStatus = result
    ? "ready"
    : inflight.current.has(key)
      ? "loading"
      : failed.current.has(key)
        ? "error"
        : "idle";
  return { result, status, prepare, ensure, retry };
}
